/**
 * The three cards' pictures, as SVG. React draws each one once; the story
 * renderer (`wee-render.ts`) moves and recolours the parts every frame by
 * their `data-part` names, so the clock never re-renders React.
 *
 * Houses are the brand mark filled with a vertical ramp across its ink
 * (objectBoundingBox, which the baked path makes exactly the ink); colour
 * changes rewrite the ramp's five stops rather than stacking copies.
 */

import React from 'react';
import { colors } from '@config/colors';
import { HOUSE_H, HOUSE_PATH, HOUSE_W, STAR_PATH } from './house-mark';
import {
  OPTIONS,
  RAMP_ENACT,
  RAMP_EXPLORE,
  RAMP_GLOW,
  RAMP_LANTERN,
  STOP_OFFSETS,
  TINT_STOPS,
  rampAt,
  type Ramp,
} from './wee-palette';
import { ART } from './wee-poses';
import type { LoopBeat } from './wee-timeline';

type ArtProps = {
  /** Unique per instance; prefixes every gradient and filter id. */
  id: string;
  /** A window onto the art box, as an SVG viewBox — to show just the house. Defaults to the whole box. */
  viewBox?: string;
};

const url = (id: string) => `url(#${id})`;

const Art: React.FC<{ beat: LoopBeat; viewBox?: string; children: React.ReactNode }> = ({
  beat,
  viewBox,
  children,
}) => {
  return (
    <svg
      viewBox={viewBox ?? `0 0 ${ART.w} ${ART.h}`}
      data-art={beat}
      aria-hidden="true"
      focusable="false"
      overflow="visible"
    >
      {children}
    </svg>
  );
};

/** A vertical ramp across the mark's ink: five stops the renderer can recolour. */
const RampGradient: React.FC<{ id: string; ramp: Ramp; part?: string }> = ({ id, ramp, part }) => (
  <linearGradient id={id} x1="0" y1="0" x2="0" y2="1" data-part={part}>
    {rampAt([ramp], 0).map((c, i) => (
      <stop key={i} offset={STOP_OFFSETS[i]} stopColor={c} />
    ))}
  </linearGradient>
);

/** The options' colour, wider than the mark so it can drift across it. */
const TintGradient: React.FC<{ id: string }> = ({ id }) => (
  <linearGradient id={id} data-part="tint-grad" x1="-1" y1="0" x2="2" y2="0">
    {TINT_STOPS.map((c, i) => (
      <stop key={i} offset={i / (TINT_STOPS.length - 1)} stopColor={c} />
    ))}
  </linearGradient>
);

/** Grounds a pale house on pale glass — the mock's soft shadow under the mark. */
const LiftShadow: React.FC<{ id: string }> = ({ id }) => (
  <filter id={id} x="-20%" y="-20%" width="140%" height="160%">
    <feDropShadow
      dx="0"
      dy="1.5"
      stdDeviation="2.2"
      floodColor={colors.brand.exxxxtraDark}
      floodOpacity="0.28"
    />
  </filter>
);

/**
 * White light needs something darker around it to read on pale glass:
 * a faint deep-water glow, the way the app's glints read against the
 * water rather than against white.
 */
const GlintHalo: React.FC<{ id: string; color: string }> = ({ id, color }) => (
  <filter id={id} x="-60%" y="-60%" width="220%" height="220%">
    <feDropShadow dx="0" dy="0" stdDeviation="0.1" floodColor={color} floodOpacity="0.9" />
  </filter>
);

const Glints: React.FC<{ id: string; prefix: string; count: number }> = ({ id, prefix, count }) => (
  <>
    {Array.from({ length: count }, (_, i) => (
      <path
        key={i}
        data-part={`${prefix}-${i}`}
        d={STAR_PATH}
        fill={colors.wee.glint}
        filter={url(id)}
        opacity="0"
      />
    ))}
  </>
);

/** A house: the mark in its fill, with an optional drifting-colour pass over it. */
const House: React.FC<{
  part: string;
  fill: string;
  tint?: { part: string; gradient: string };
  shadow?: string;
  children?: React.ReactNode;
}> = ({ part, fill, tint, shadow, children }) => (
  <g data-part={part}>
    <path d={HOUSE_PATH} fill={url(fill)} filter={shadow ? url(shadow) : undefined} />
    {tint ? <path data-part={tint.part} d={HOUSE_PATH} fill={url(tint.gradient)} opacity="0" /> : null}
    {children}
  </g>
);

// --- Wish --------------------------------------------------------------

export const WishArt: React.FC<ArtProps> = ({ id, viewBox }) => (
  <Art beat="wish" viewBox={viewBox}>
    <defs>
      <radialGradient id={`${id}-pool`}>
        <stop offset="0" stopColor={colors.wee.depthPool} />
        <stop offset="1" stopColor={colors.wee.depthPoolClear} />
      </radialGradient>
      <radialGradient id={`${id}-halo`}>
        <stop offset="0" stopColor={colors.wee.lanternHaloInner} />
        <stop offset="0.52" stopColor={colors.wee.lanternHalo} />
        <stop offset="0.74" stopColor={colors.wee.lanternHaloMid} />
        <stop offset="1" stopColor={colors.wee.lanternHaloClear} />
      </radialGradient>
      <RampGradient id={`${id}-fill`} ramp={RAMP_LANTERN} />
      <TintGradient id={`${id}-tint`} />
      <LiftShadow id={`${id}-lift`} />
      <GlintHalo id={`${id}-glint`} color={colors.brand.exxxtraDark} />
    </defs>
    <circle data-part="pool" r="0" fill={url(`${id}-pool`)} opacity="0" />
    <circle data-part="halo" r="0" fill={url(`${id}-halo`)} opacity="0" />
    <House
      part="house"
      fill={`${id}-fill`}
      shadow={`${id}-lift`}
      tint={{ part: 'tint', gradient: `${id}-tint` }}
    />
    <Glints id={`${id}-glint`} prefix="glint" count={4} />
  </Art>
);

// --- Explore -----------------------------------------------------------

const BarGradients: React.FC<{ id: string }> = ({ id }) => (
  <>
    {OPTIONS.map((o, i) => (
      <linearGradient key={i} id={`${id}-bar-${i}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={o.pale} />
        <stop offset="1" stopColor={o.deep} />
      </linearGradient>
    ))}
  </>
);

export const ExploreArt: React.FC<ArtProps> = ({ id, viewBox }) => (
  <Art beat="explore" viewBox={viewBox}>
    <defs>
      <RampGradient id={`${id}-rest`} ramp={RAMP_EXPLORE} />
      <TintGradient id={`${id}-tint`} />
      {OPTIONS.map((_, i) => (
        <RampGradient key={i} id={`${id}-cand-${i}`} ramp={RAMP_LANTERN} part={`cand-fill-${i}`} />
      ))}
      <BarGradients id={id} />
      <LiftShadow id={`${id}-lift`} />
    </defs>
    <House part="rest" fill={`${id}-rest`} shadow={`${id}-lift`} />
    {OPTIONS.map((_, i) => (
      <House
        key={i}
        part={`cand-${i}`}
        fill={`${id}-cand-${i}`}
        tint={{ part: `cand-tint-${i}`, gradient: `${id}-tint` }}
      />
    ))}
    {OPTIONS.map((o, i) => (
      <g key={i} data-part={`col-${i}`} opacity="0">
        {[0, 1, 2].map((b) => (
          <rect key={b} data-part={`bar-${i}-${b}`} fill={url(`${id}-bar-${i}`)} />
        ))}
        {[o.deep, o.pale, colors.wee.lanternEdge].map((c, k) => (
          <path key={k} data-part={`slice-${i}-${k}`} fill={c} />
        ))}
      </g>
    ))}
  </Art>
);

// --- Enact -------------------------------------------------------------

export const EnactArt: React.FC<ArtProps> = ({ id, viewBox }) => (
  <Art beat="enact" viewBox={viewBox}>
    <defs>
      <RampGradient id={`${id}-fill`} ramp={RAMP_ENACT} part="fill" />
      <RampGradient id={`${id}-glow-fill`} ramp={RAMP_GLOW} />
      <radialGradient id={`${id}-burst`}>
        <stop offset="0" stopColor={colors.wee.burstCore} />
        <stop offset="0.45" stopColor={colors.wee.burstGold} />
        <stop offset="1" stopColor={colors.wee.burstClear} />
      </radialGradient>
      <linearGradient id={`${id}-shimmer`} data-part="shimmer-grad" x1="0" y1="0" x2="0.5" y2="0.6">
        <stop offset="0" stopColor={colors.wee.markShimmerClear} />
        <stop offset="0.5" stopColor={colors.wee.markShimmer} />
        <stop offset="1" stopColor={colors.wee.markShimmerClear} />
      </linearGradient>
      <clipPath id={`${id}-clip`}>
        <path d={HOUSE_PATH} />
      </clipPath>
      <filter id={`${id}-blur`} x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="5" />
      </filter>
      <LiftShadow id={`${id}-lift`} />
      <GlintHalo id={`${id}-spark`} color={colors.accent.yellow.exxtraDark} />
    </defs>
    <circle data-part="burst" r="0" fill={url(`${id}-burst`)} opacity="0" />
    <g data-part="glow" opacity="0">
      <path d={HOUSE_PATH} fill={url(`${id}-glow-fill`)} filter={url(`${id}-blur`)} />
    </g>
    <House part="house" fill={`${id}-fill`} shadow={`${id}-lift`}>
      <rect
        data-part="shimmer"
        width={HOUSE_W}
        height={HOUSE_H}
        fill={url(`${id}-shimmer`)}
        clipPath={url(`${id}-clip`)}
        opacity="0"
      />
    </House>
    <Glints id={`${id}-spark`} prefix="spark" count={6} />
  </Art>
);

// --- the folded card's house -------------------------------------------

const RESTING: Record<LoopBeat, Ramp> = {
  wish: RAMP_LANTERN,
  explore: RAMP_EXPLORE,
  enact: RAMP_ENACT,
};

/**
 * A stacked card that is not open shows just its house and its name:
 * the mark at rest, in the card's own colour, as the mock draws it.
 */
export const MiniHouse: React.FC<{ id: string; beat: LoopBeat }> = ({ id, beat }) => (
  <svg viewBox={`-6 -6 ${HOUSE_W + 12} ${HOUSE_H + 12}`} aria-hidden="true" focusable="false">
    <defs>
      <RampGradient id={`${id}-mini`} ramp={RESTING[beat]} />
      <LiftShadow id={`${id}-mini-lift`} />
    </defs>
    <path d={HOUSE_PATH} fill={url(`${id}-mini`)} filter={url(`${id}-mini-lift`)} />
  </svg>
);
